import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { EditableStudyMaterialTypeEnum } from '@edtech-ui/constants/editable-material-type.enum';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { CompleteCourseDataAI, CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { UpdateCourseDataAI } from '@edtech-ui/core/models/ai-course/edit-course';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { EditCourseTitleComponent } from '@edtech-ui/dashboard/manual-course-creation/edit-course-title/edit-course-title.component';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { DeleteDialogBoxComponent } from '../delete-dialog-box/delete-dialog-box.component';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { HourglassLoaderCourseComponent } from '@edtech-ui/shared/hourglass-loader-course/hourglass-loader-course.component';

@Component({
	selector: 'app-classroom-course',
	standalone: true,
	imports: [MatMenuModule, MatIconModule, CommonModule, MatButtonModule, MatProgressSpinnerModule, MatTooltipModule ,
		HourglassLoaderCourseComponent
	],
	templateUrl: './classroom-course.component.html',
	styleUrl: './classroom-course.component.scss'
})
export class ClassroomCourseComponent {

	_courseData: CompleteCourseDataAI[] = [];
	_totalCourseCount: number = 0;
	classroomId: number = 0;
	courseId: number | string | undefined;
	subscription = new Subscription();
	EditableStudyMaterialTypeEnum = EditableStudyMaterialTypeEnum;
	filterOpen: boolean = false;
	selectedQuizFilter: CourseStatusEnum = CourseStatusEnum.All;
	selectedCourseFilter: CourseStatusEnum = CourseStatusEnum.All
	_allCourseData: CompleteCourseDataAI[] = [];
	courseStatuses = [CourseStatusEnum.All, CourseStatusEnum.Published, CourseStatusEnum.Draft];
	isLoading: boolean = false;
	isModalOpen = false;
	isCourseCreationModalOpen = false;
	CourseStatusEnum = CourseStatusEnum;
	isCreating = false;
	StudyMaterialTypeEnum = StudyMaterialTypeEnum;
	pageCourse = 1;
	limitCourse = 10;
	totalPagesCourse = 0;

	constructor(
		private router: Router,
		private _courseService: CourseManagementService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private _toastr: ToastrService,
	) {
		this.subscription.add(this._courseService.reload$.pipe(tap(() => this.getAllcourses())).subscribe());
	}

	ngOnInit(): void {
		this.route.parent?.paramMap.subscribe(params => {
			const classroomId = params.get('id');
			const courseId = params.get('courseId');
			if (classroomId) {
				this.classroomId = +classroomId;
			}
			if (courseId) {
				this.courseId = +courseId
			}
			this.getAllcourses();
		});
	}

	getAllcourses() {
		this.isLoading = true;
		if (this.classroomId === undefined || this.classroomId === null) {
			console.error('classroomId is missing');
			return;
		}
		this.subscription.add(
			this._courseService.getCourses(this.classroomId!,this.pageCourse,this.limitCourse,'created_at','desc','all').subscribe({
				next: (response) => {
					if (response.Success && response.Data) {
						this.isLoading = false;
						this._courseData = response.Data.data;
						this._totalCourseCount = response.Data.total;
						this._allCourseData = response.Data.data;
						this.applyCourseFilter();
					} else {
						this.isLoading = false;
						this._toastr.error('Something went wrong while fetching courses');
					}
				},
				error: () => {
					this._toastr.error('Something went wrong while fetching courses');
				}
			}));
	}

	goTocoursePage(courseId: string | number) {
		this._courseService.getCoursesByCourseId(courseId).subscribe({
			next: (response: CompleteSyllabusResponseAI) => {
				if (!response?.Data) {
					this._toastr.error('Invalid course response');
					return;
				}

				const is_ai = response.Data?.is_ai ?? false;

				if (is_ai) {
					this.router.navigate([
						'teacher-dashboard',
						'ai-course-result',
						this.classroomId,
						courseId
					]);
				} else {
					this.router.navigate([
						'/teacher-dashboard',
						this.classroomId,
						'manual-course-creation',
						'course-preview'
					], { queryParams: { courseId } });
				}
			},
			error: () => {
				this._toastr.error('Something went wrong while fetching courses');
			}
		});
	}


	toggleFilter() {
		this.filterOpen = !this.filterOpen;
	}


	openEditTitleBox(materialEditId: string | number, materialEditTitle: string,
		materialEditType: EditableStudyMaterialTypeEnum) {
		const dialogRef = this.dialog.open(EditCourseTitleComponent, {
			width: '500px',
			data: {
				materialEditId, editTitle: materialEditTitle, label: materialEditType
			}
		});
		dialogRef.afterClosed().subscribe((updatedData: any) => {
			if (!updatedData) return;

			if (materialEditType === EditableStudyMaterialTypeEnum.Course) {
				const updatedTitle = typeof updatedData === 'string' ? updatedData : updatedData.editTitle;
				if (updatedTitle.trim() !== materialEditTitle.trim()) {
					const payload: UpdateCourseDataAI = {
						course_id: materialEditId,
						course_title: updatedTitle
					};
					this.postUpdateCourse(payload, materialEditId);
				}
			}
		})
	}

	postUpdateCourse(payload: UpdateCourseDataAI, courseId: string | number) {
		this.subscription.add(
			this._courseService.postUpdateCourse(payload, courseId,null,null).pipe(
				tap(response => {
					if (response.Success) {
						this.getAllcourses();
						this._toastr.success('Course name update successfully');
					}
				}),
				catchError(() => {
					this._toastr.error('something went wrong while updating quiz name');
					return of(null);
				}),
			).subscribe());
	}

	openDeleteDialogBox(materialId: string, materialType: StudyMaterialTypeEnum) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { materialId, materialType }
		}).afterClosed().subscribe(result => {
			if (result == true) {
				if (materialType === StudyMaterialTypeEnum.Course) {
					this.deleteCoursesById(materialId);
				}
			}
		});
	}

	deleteCoursesById(courseId: string | number) {
		this.subscription.add(
			this._courseService.deleteCourseById(courseId).subscribe({
				next: () => {
					this._toastr.success('Course deleted successfully');
					this.getAllcourses();
				},
				error: () => {
					this._toastr.error('Failed to delete course');
				}
			})
		);
	}

	onFilterChange(status: CourseStatusEnum, type: StudyMaterialTypeEnum) {
		this.filterOpen = false;
		if (type === StudyMaterialTypeEnum.Course) {
			this.selectedCourseFilter = status;
			this.applyCourseFilter();
		}
	}

	applyCourseFilter() {
		if (this.selectedCourseFilter === CourseStatusEnum.All) {
			this._courseData = [...this._allCourseData];
		} else {
			this._courseData = this._allCourseData.filter(c => c.status === this.selectedCourseFilter);
		}
	}

	openModal() {
		this.isModalOpen = true;
	}

	closeModal() {
		this.isModalOpen = false;
	}

	goToCoursePage() {
		this.router.navigate(['/teacher-dashboard/', this.classroomId, 'manual-course-creation', 'course-creation-form']);
	}

	generateWithAI() {
		this.router.navigate(['../ai-creation'], { relativeTo: this.route });
	}

	openCourseCreationModal() {
		this.isCourseCreationModalOpen = true;
	}

	closeCourseCreationModal() {
		this.isCourseCreationModalOpen = false;
	}

	convertToIST(utcDateString: string): string {
		if (!utcDateString) return '';
		const date = new Date(utcDateString + 'Z');
		return new Intl.DateTimeFormat('en-IN', {
			timeZone: 'Asia/Kolkata',
			dateStyle: 'medium',
			timeStyle: 'short'
		}).format(date);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}